import { Button, Input, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useMemo, useState, type ComponentProps } from "react";

import { useUserStore } from "@/views/stores";
import {
    Dialog as ZhDialog,
    InputNumber as ZhInputNumber,
    Select as ZhSelect,
    Upload as ZhUpload,
    type UploadRequestOptions,
    type UploadUserFile
} from "zhihao-ui-react";

import request from "@/api";
import RequiredMark from "@/views/components/RequiredMark";

export interface RechargeCustomerOption {
    id: number;
    institutionName?: string;
    mobile?: string;
    realName?: string;
    code?: string;
    specialZoneName?: string;
}

export interface RechargeZoneOption {
    id?: number;
    /** 旧接口实际返回 code，新接口部分环境仍返回 id。 */
    code?: number | string;
    label?: string;
    name?: string;
}

export interface RechargeAccountInfo {
    id?: string | number;
    balanceAmount?: number | string;
}

export interface ZoneRechargePayload {
    customerId: number;
    specialZoneId: number;
    accountId: string;
    rechargeAmount: number;
    remark: string;
    certificateId?: number;
}

export interface ZoneRechargeFormDialogProps {
    open: boolean;
    onCancel: () => void;
    onSuccess?: () => void;
    searchCustomers?: (keyword: string) => Promise<RechargeCustomerOption[]>;
    loadZones?: (params: { customerId: number; zoneName: string }) => Promise<RechargeZoneOption[]>;
    findAccount?: (params: { customerId: number; zoneId: number }) => Promise<RechargeAccountInfo>;
    submitRecharge?: (payload: ZoneRechargePayload) => Promise<unknown>;
}

const RECHARGE_AMOUNT_MIN = -100000000;
const RECHARGE_AMOUNT_MAX = 100000000;
const CERTIFICATE_MAX_SIZE = 3 * 1024 * 1024;
const CERTIFICATE_ACCEPT = ".jpeg,.jpg,.png,.gif,.webp";
const CERTIFICATE_EXTENSIONS = new Set(["jpeg", "jpg", "png", "gif", "webp"]);

// beta.13 的运行时兼容旧 Vue MoneyInput，但类型声明未暴露 controls 字段。
type RechargeInputNumberProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const rechargeInputNumberProps: RechargeInputNumberProps = {
    controls: false,
    max: RECHARGE_AMOUNT_MAX,
    min: RECHARGE_AMOUNT_MIN,
    precision: 2
};

/** 复用 Vue 充值主体选择接口。 */
export async function searchZoneRechargeCustomers(keyword: string): Promise<RechargeCustomerOption[]> {
    const response = await request.service.get<RechargeCustomerOption[]>("/admin/special-zone-account/selector", {
        params: { keyword }
    });
    return response.data;
}

/** 复用 Vue 充值专区选择接口。 */
export async function loadZoneRechargeZones(params: {
    customerId: number;
    zoneName: string;
}): Promise<RechargeZoneOption[]> {
    const response = await request.service.get<RechargeZoneOption[]>("/admin/special-zone-account/zone-selector", {
        params
    });
    return response.data;
}

/** 复用 Vue 用户与专区联合查询余额接口。 */
export async function findZoneRechargeAccount(params: {
    customerId: number;
    zoneId: number;
}): Promise<RechargeAccountInfo> {
    const response = await request.service.get<RechargeAccountInfo>("/admin/special-zone-account/find-by-uk", {
        params
    });
    return response.data;
}

/** 复用 Vue 专区充值提交接口。 */
export async function submitZoneRecharge(payload: ZoneRechargePayload): Promise<unknown> {
    const response = await request.service.post("/admin/special-zone-recharge/recharge", payload);
    return response.data;
}

/** 兼容专区选择接口的 id/code 两种字段，保证下拉选中后能查询账户余额。 */
export function getZoneRechargeOptionValue(option: RechargeZoneOption): number | string | undefined {
    return option.id ?? option.code;
}

/** 保留 Vue 充值主体下拉的机构/手机号/编码展示规则。 */
function getCustomerOptionLabel(option: RechargeCustomerOption): string {
    return `${option.institutionName?.trim() || "--"} | ${option.mobile?.trim() || "--"} | ${option.code?.trim() || "--"}`;
}

/** 复用旧 Vue 上传凭证入口的云上传图标，避免引入额外图标依赖。 */
function UploadCertificateIcon() {
    return (
        <svg aria-hidden="true" height="16" viewBox="0 0 1024 1024" width="16">
            <path
                d="M832 416a224 224 0 0 0-430.6-84.5A176 176 0 0 0 224 672h128v-64h-128a112 112 0 1 1 77-192.5l38 35.8 12.7-49.7A160 160 0 0 1 664 448v32h48a144 144 0 0 1 0 288H544v64h168a208 208 0 0 0 120-376zM480 608l-80-80-45.3 45.3L448 666.6V832h64V666.6l93.3-93.3L560 528z"
                fill="currentColor"
            />
        </svg>
    );
}

/** 专区充值弹窗，迁移 Vue 的主体、专区、余额查询和金额校验状态机。 */
export function ZoneRechargeFormDialog({
    open,
    onCancel,
    onSuccess,
    searchCustomers = searchZoneRechargeCustomers,
    loadZones = loadZoneRechargeZones,
    findAccount = findZoneRechargeAccount,
    submitRecharge = submitZoneRecharge
}: ZoneRechargeFormDialogProps) {
    const banButton = useUserStore(state => state.banButton);
    const [customerKeyword, setCustomerKeyword] = useState("");
    const [customers, setCustomers] = useState<RechargeCustomerOption[]>([]);
    const [customerId, setCustomerId] = useState<number | undefined>();
    const [zoneId, setZoneId] = useState<number | undefined>();
    const [zones, setZones] = useState<RechargeZoneOption[]>([]);
    const [accountId, setAccountId] = useState("");
    const [balance, setBalance] = useState<number | null>(null);
    const [amount, setAmount] = useState("");
    const [remark, setRemark] = useState("");
    const [certificateId, setCertificateId] = useState<number | undefined>();
    const [certificateFiles, setCertificateFiles] = useState<UploadUserFile[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCustomerKeyword("");
        setCustomers([]);
        setCustomerId(undefined);
        setZoneId(undefined);
        setZones([]);
        setAccountId("");
        setBalance(null);
        setAmount("");
        setRemark("");
        setCertificateId(undefined);
        setCertificateFiles([]);
        setErrors([]);
    }, [open]);

    /** 对齐 Vue 远程 Select：仅在输入非空关键词时请求主体候选。 */
    const search = async (keyword = customerKeyword) => {
        const normalizedKeyword = keyword.trim();
        setCustomerKeyword(keyword);
        if (!normalizedKeyword) {
            // 远程搜索清空时仍保留已选主体，避免主体显示消失但专区候选和余额状态残留。
            setCustomers(current => {
                if (!customerId) return [];
                return current.filter(item => item.id === customerId);
            });
            return;
        }
        try {
            const nextCustomers = await searchCustomers(normalizedKeyword);
            setCustomers(current => {
                // 保留当前已选项，让 Select 在搜索词清空或异步查询无结果时仍能显示主体。
                const selected = current.find(item => item.id === customerId);
                if (!selected || nextCustomers.some(item => item.id === selected.id)) return nextCustomers;
                return [selected, ...nextCustomers];
            });
        } catch {
            // 搜索失败也保留已选主体，避免网络异常把当前弹窗变成主体空白的半残状态。
            setCustomers(current => (customerId ? current.filter(item => item.id === customerId) : []));
            setErrors(["充值主体加载失败"]);
        }
    };

    /** 选择主体后加载其可充值专区，并清空旧账户余额。 */
    const selectCustomer = async (value: string) => {
        const nextCustomerId = value ? Number(value) : undefined;
        const selected = customers.find(item => item.id === nextCustomerId);
        setCustomerId(nextCustomerId);
        setZoneId(undefined);
        setAccountId("");
        setBalance(null);
        if (!nextCustomerId || !selected) {
            // 清空主体时同步清理候选，避免下一次打开弹窗复用旧主体。
            setCustomers([]);
            setZones([]);
            return;
        }
        try {
            setZones(await loadZones({ customerId: nextCustomerId, zoneName: selected.specialZoneName || "" }));
        } catch {
            setZones([]);
            setErrors(["充值专区加载失败"]);
        }
    };

    /** 选择专区后查询账户编号和充值前余额。 */
    const selectZone = async (value: string) => {
        const nextZoneId = value ? Number(value) : undefined;
        setZoneId(nextZoneId);
        if (!customerId || !nextZoneId) {
            setAccountId("");
            setBalance(null);
            return;
        }
        try {
            const account = await findAccount({ customerId, zoneId: nextZoneId });
            setAccountId(String(account.id || ""));
            const nextBalance = Number(account.balanceAmount);
            setBalance(Number.isFinite(nextBalance) ? nextBalance : 0);
        } catch {
            setAccountId("");
            setBalance(null);
            setErrors(["该用户在当前专区暂无账户"]);
        }
    };

    const selectedCustomer = useMemo(() => customers.find(item => item.id === customerId), [customerId, customers]);

    /** 对齐 Vue SingleFileUpload 的图片类型和 3MB 限制。 */
    const validateCertificateFile = (file: File): boolean => {
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        if (!CERTIFICATE_EXTENSIONS.has(extension) || file.size > CERTIFICATE_MAX_SIZE) {
            setErrors(["凭证仅支持 jpeg/jpg/png/gif/webp 图片且大小不能超过3MB"]);
            return false;
        }
        return true;
    };

    /** 使用现有文件服务上传凭证，并把返回的文件 ID 写入 Vue certificateId 字段。 */
    const uploadCertificate = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fileName", file.name);
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const nextCertificateId = Number(uploadedFile.id);
            if (!Number.isInteger(nextCertificateId) || nextCertificateId <= 0) {
                throw new Error("invalid certificate id");
            }
            setCertificateId(nextCertificateId);
            setCertificateFiles([
                {
                    uid: nextCertificateId,
                    name: uploadedFile.name || file.name,
                    status: "success",
                    url: uploadedFile.path
                }
            ]);
            setErrors([]);
            onSuccess(uploadedFile);
        } catch (error) {
            setErrors(["凭证上传失败"]);
            onError(error instanceof Error ? error : new Error("certificate upload failed"));
        }
    };

    const submit = async () => {
        const nextErrors: string[] = [];
        const nextAmount = Number(amount);
        if (!customerId) nextErrors.push("请选择充值主体");
        if (!zoneId) nextErrors.push("请选择充值专区");
        if (amount === "") nextErrors.push("请输入充值金额");
        else if (!Number.isFinite(nextAmount) || nextAmount < -100000000 || nextAmount > 100000000) {
            nextErrors.push("金额取值范围为-100000000~100000000");
        }
        if (nextErrors.length) {
            setErrors(nextErrors);
            return;
        }
        setLoading(true);
        try {
            await submitRecharge({
                customerId: customerId!,
                specialZoneId: zoneId!,
                accountId,
                rechargeAmount: nextAmount,
                remark,
                certificateId
            });
            onSuccess?.();
            onCancel();
        } catch {
            setErrors(["充值失败"]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ZhDialog alignCenter open={open} title="充值" destroyOnClose footer={null} width={520} onCancel={onCancel}>
            <div className="zone-recharge-dialog">
                {errors.length ? (
                    <Alert className="mb-4" message={errors.join("；")} role="alert" type="error" />
                ) : null}
                <label className="zone-recharge-dialog-form-item">
                    <span className="zone-recharge-dialog-form-label">
                        <RequiredMark />
                        充值主体
                    </span>
                    <ZhSelect
                        aria-label="充值主体"
                        className="zone-recharge-dialog-form-control"
                        filterOption={false}
                        hideEmptyBeforeSearch
                        options={customers.map(item => ({
                            label: getCustomerOptionLabel(item),
                            value: item.id
                        }))}
                        placeholder="请输入机构名称/用户ID/手机号查询"
                        showSearch
                        value={customerId}
                        onChange={value => void selectCustomer(value == null ? "" : String(value))}
                        onSearch={value => void search(value)}
                    />
                </label>
                <div aria-label="主体信息" className="zone-recharge-dialog-form-item">
                    <span className="zone-recharge-dialog-form-label">主体信息</span>
                    <div className="zone-recharge-dialog-form-control zone-recharge-dialog-customer-info">
                        {selectedCustomer ? (
                            <>
                                <div>
                                    {selectedCustomer.realName?.trim() || "--"} ({selectedCustomer.code?.trim() || "--"}
                                    )
                                    <span className="zone-recharge-dialog-customer-mobile">
                                        {selectedCustomer.mobile?.trim() || "--"}
                                    </span>
                                </div>
                                <div>{selectedCustomer.institutionName?.trim() || "--"}</div>
                            </>
                        ) : null}
                    </div>
                </div>
                <label className="zone-recharge-dialog-form-item">
                    <span className="zone-recharge-dialog-form-label">
                        <RequiredMark />
                        充值专区
                    </span>
                    <ZhSelect
                        aria-label="充值专区"
                        className="zone-recharge-dialog-form-control"
                        options={zones.map(item => ({
                            label: item.label || item.name || "--",
                            value: getZoneRechargeOptionValue(item)
                        }))}
                        placeholder="请选择充值专区"
                        value={zoneId}
                        onChange={value => void selectZone(value == null ? "" : String(value))}
                    />
                </label>
                <div className="zone-recharge-dialog-form-item">
                    <span className="zone-recharge-dialog-form-label">充值前余额(元)</span>
                    <div
                        aria-label="充值前余额"
                        className="zone-recharge-dialog-form-control zone-recharge-dialog-balance"
                    >
                        {balance == null ? "--" : balance.toFixed(2)}
                    </div>
                </div>
                <label className="zone-recharge-dialog-form-item">
                    <span className="zone-recharge-dialog-form-label">
                        <RequiredMark />
                        充值金额(元)
                    </span>
                    <ZhInputNumber
                        aria-label="充值金额"
                        className="zone-recharge-dialog-form-control"
                        placeholder="请输入"
                        {...rechargeInputNumberProps}
                        value={amount}
                        onChange={value => setAmount(String(value))}
                    />
                </label>
                <label className="zone-recharge-dialog-form-item">
                    <span className="zone-recharge-dialog-form-label">备注</span>
                    <Input.TextArea
                        aria-label="充值备注"
                        className="zone-recharge-dialog-form-control zone-recharge-dialog-remark"
                        maxLength={200}
                        placeholder="请输入"
                        showCount
                        value={remark}
                        onChange={event => setRemark(event.target.value)}
                    />
                </label>
                <div className="zone-recharge-dialog-form-item">
                    <span className="zone-recharge-dialog-form-label">上传凭证</span>
                    <div className="zone-recharge-dialog-form-control">
                        <ZhUpload
                            accept={CERTIFICATE_ACCEPT}
                            action=""
                            fileList={certificateFiles}
                            limit={1}
                            beforeUpload={validateCertificateFile}
                            httpRequest={uploadCertificate}
                            onRemove={() => {
                                setCertificateId(undefined);
                                setCertificateFiles([]);
                            }}
                        >
                            <Button htmlType="button" icon={<UploadCertificateIcon />} type="link">
                                上传
                            </Button>
                        </ZhUpload>
                    </div>
                </div>
                <Space className="zone-recharge-dialog-footer">
                    <Button onClick={onCancel}>取消</Button>
                    <Button
                        aria-label="提交充值"
                        disabled={banButton}
                        loading={loading}
                        type="primary"
                        onClick={() => void submit()}
                    >
                        提交
                    </Button>
                </Space>
            </div>
        </ZhDialog>
    );
}

export default ZoneRechargeFormDialog;
